import { MapPin, Mic, Plus } from "lucide-react";
import { Link } from "react-router";
import { useGetList } from "ra-core";
import { SellerAvatar } from "./SellerAvatar";
import { StarRating } from "./StarRating";
import {
  type Meeting,
  formatMeetingDate,
  formatMeetingTime,
  linesToList,
  sourceLabel,
} from "./meetingsUtils";

/**
 * MeetingsTimeline — lista compacta de juntas reusable.
 *
 * La usan CompanyShow (filtrada por company_id), ContactShow (filtrada por
 * los meetings donde el contacto fue attendee), y DealShow (filtrada por
 * deal_id). El layout es una cards stack con foto del vendedor + título +
 * fecha + score, lo suficiente para escanear histórico rápido.
 *
 * Si está vacío y se proveyó `createLink`, muestra CTA para crear la
 * primera junta con el contexto pre-llenado.
 */
export function MeetingsTimeline({
  filter,
  emptyTitle = "Sin juntas registradas",
  emptyDescription = "Aún no se ha capturado ninguna junta. Crea una para llevar el historial.",
  createLink,
  limit = 20,
  resource = "meetings_summary",
}: {
  filter: Record<string, any>;
  emptyTitle?: string;
  emptyDescription?: string;
  /** state que se pasa a /meetings/create para prellenar empresa/deal */
  createLink?: { record?: { company_id?: number; deal_id?: number } };
  limit?: number;
  resource?: "meetings_summary" | "meetings";
}) {
  const { data: meetings, isPending } = useGetList<Meeting>(resource, {
    pagination: { page: 1, perPage: limit },
    sort: { field: "occurred_at", order: "DESC" },
    filter,
  });

  if (isPending) {
    return (
      <p className="text-sm py-4" style={{ color: "var(--musco-muted)" }}>
        Cargando juntas…
      </p>
    );
  }

  if (!meetings || meetings.length === 0) {
    return (
      <div
        className="rounded-xl p-6 text-center"
        style={{
          background: "var(--musco-panel2)",
          border: "1px dashed var(--musco-line)",
        }}
      >
        <Mic
          size={24}
          style={{ margin: "0 auto 6px", color: "var(--musco-muted)" }}
        />
        <h4 className="m-0 font-semibold text-sm mb-1">{emptyTitle}</h4>
        <p
          className="m-0 text-xs mb-3"
          style={{ color: "var(--musco-muted)" }}
        >
          {emptyDescription}
        </p>
        <Link
          to="/meetings/create"
          state={createLink}
          className="musco-btn musco-btn-a"
        >
          <Plus size={14} /> Capturar junta
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Link
          to="/meetings/create"
          state={createLink}
          className="musco-btn musco-btn-g"
        >
          <Plus size={14} /> Nueva junta
        </Link>
      </div>
      {meetings.map((m) => (
        <MeetingRow key={m.id} meeting={m} />
      ))}
    </div>
  );
}

function MeetingRow({ meeting }: { meeting: Meeting }) {
  const topics = linesToList(meeting.topics);
  const headline =
    meeting.title || topics[0] || meeting.summary || "Junta sin título";
  const location = meeting.location || meeting.company_city || "";
  return (
    <Link
      to={`/meetings/${meeting.id}/show`}
      className="musco-panel p-4 flex items-center gap-3 no-underline hover:translate-y-[-1px] transition-transform"
      style={{ color: "var(--musco-text)" }}
    >
      <SellerAvatar
        id={meeting.sales_id}
        name={meeting.sales_name}
        size={36}
        imageUrl={meeting.sales_avatar?.src}
      />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{headline}</div>
        <div
          className="text-xs flex items-center gap-2 flex-wrap mt-0.5"
          style={{ color: "var(--musco-muted)" }}
        >
          <span>{formatMeetingDate(meeting.occurred_at)}</span>
          <span>·</span>
          <span>{formatMeetingTime(meeting.occurred_at)}</span>
          {location ? (
            <>
              <span>·</span>
              <span className="inline-flex items-center gap-0.5 truncate max-w-[160px]" title={location}>
                <MapPin size={11} /> {location}
              </span>
            </>
          ) : null}
          <span>·</span>
          <span>{sourceLabel(meeting.source)}</span>
          {meeting.attendee_count ? (
            <>
              <span>·</span>
              <span>
                {meeting.attendee_count} asistente
                {meeting.attendee_count !== 1 ? "s" : ""}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        <StarRating
          value={meeting.rating ?? 0}
          size={14}
          readOnly
        />
        <span
          className="text-[10px] tnums font-semibold"
          style={{ color: "var(--musco-muted)" }}
        >
          {meeting.rating ? `${meeting.rating}/5` : "—"}
        </span>
      </div>
    </Link>
  );
}
